<template>
  <div>
    <!-- Header buttons -->
    <div class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <button class="btn btn-primary btn-close btn-sm" @click="$emit('close')">Close</button>
        <button class="btn btn-success btn-sm" :disabled="saving" @click="emitSave">
          <span v-if="!saving">Save</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </div>
    </div>

    <!-- Title header -->
    <div class="title-hdr bg-dark p-2 text-white">
      <p class="mb-0">
        Manage Role Users:
        <span class="badge bg-success">{{ roleDisplayName }}</span>
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex align-items-center justify-content-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <div v-if="errorText" class="alert alert-danger d-flex align-items-center" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <div>{{ errorText }}</div>
      </div>

      <div class="container-fluid">
        <div class="row g-3 align-items-stretch">

          <!-- LEFT: Current Users -->
          <div class="col-12 col-lg-5">
            <div class="card h-100">
              <div class="card-header fw-semibold">Current Users</div>
              <div class="card-body p-0">
                <div class="table-responsive" @dragover.prevent @drop="onDropTo('left')">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th style="width:2.5rem;">
                        <input type="checkbox"
                               :checked="isAllLeftSelected"
                               @change="toggleSelectAll('left')">
                      </th>
                      <th>Name</th>
                      <th>Username</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="u in leftUsers"
                        :key="u.user_id"
                        draggable="true"
                        @dragstart="onDragStart('left', u)"
                        @click="toggleRow('left', u)"
                        :class="rowClass(isSelected('left', u))">
                      <td>
                        <input type="checkbox"
                               :checked="isSelected('left', u)"
                               @change.stop="toggleRow('left', u)">
                      </td>
                      <td>{{ u.name || u.display_name || '—' }}</td>
                      <td>{{ u.username || '—' }}</td>
                    </tr>
                    <tr v-if="leftUsers.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No users assigned</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer small text-muted">{{ selectedLeft.size }} selected</div>
            </div>
          </div>

          <!-- CENTER BUTTONS -->
          <div class="col-12 col-lg-2
            d-flex flex-column
            justify-content-start
            align-items-center
            gap-2
            center-controls">
          <button class="btn btn-outline-primary btn-sm w-100"
                    :disabled="selectedLeft.size === 0"
                    @click="moveSelected('left','right')">
              → Move Selected
            </button>
            <button class="btn btn-outline-primary btn-sm w-100"
                    :disabled="selectedRight.size === 0"
                    @click="moveSelected('right','left')">
              ← Move Selected
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100"
                    @click="toggleSelectAll('left')">
              {{ isAllLeftSelected ? 'Deselect All (Left)' : 'Select All (Left)' }}
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100"
                    @click="toggleSelectAll('right')">
              {{ isAllRightSelected ? 'Deselect All (Right)' : 'Select All (Right)' }}
            </button>
          </div>

          <!-- RIGHT: Available Users -->
          <div class="col-12 col-lg-5">
            <div class="card h-100">
              <div class="card-header fw-semibold">Available Users</div>
              <div class="card-body p-0">
                <div class="table-responsive" @dragover.prevent @drop="onDropTo('right')">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th style="width:2.5rem;">
                        <input type="checkbox"
                               :checked="isAllRightSelected"
                               @change="toggleSelectAll('right')">
                      </th>
                      <th>Name</th>
                      <th>Username</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="u in rightUsers"
                        :key="u.user_id"
                        draggable="true"
                        @dragstart="onDragStart('right', u)"
                        @click="toggleRow('right', u)"
                        :class="rowClass(isSelected('right', u))">
                      <td>
                        <input type="checkbox"
                               :checked="isSelected('right', u)"
                               @change.stop="toggleRow('right', u)">
                      </td>
                      <td>{{ u.name || u.display_name || '—' }}</td>
                      <td>{{ u.username || '—' }}</td>
                    </tr>
                    <tr v-if="rightUsers.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No available users</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer small text-muted">{{ selectedRight.size }} selected</div>
            </div>
          </div>

        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ManageUsers',
  props: {
    role: { type: Object, required: true },
    initialLeft: { type: Array, default: function() { return []; } },   // assigned users
    initialRight: { type: Array, default: function() { return []; } },  // available users
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    errorText: { type: String, default: '' },
  },
  data: function() {
    return {
      leftUsers: [],
      rightUsers: [],
      selectedLeft: new Set(),
      selectedRight: new Set(),
      drag: { from: null, id: null },
    };
  },
  computed: {
    roleDisplayName: function() {
      if (this.role) {
        if (this.role.role_name) return this.role.role_name;
        if (this.role.name) return this.role.name;
      }
      return 'Role';
    },
    isAllLeftSelected: function() {
      return this.leftUsers.length > 0 && this.selectedLeft.size === this.leftUsers.length;
    },
    isAllRightSelected: function() {
      return this.rightUsers.length > 0 && this.selectedRight.size === this.rightUsers.length;
    },
  },
  methods: {
    resetFromProps: function() {
      this.leftUsers = (this.initialLeft || []).map(function(u){ return Object.assign({}, u); });
      this.rightUsers = (this.initialRight || []).map(function(u){ return Object.assign({}, u); });
      this.clearSelections();
    },
    emitSave: function() {
      var userIds = this.leftUsers.map(function(u){ return u.user_id; });
      this.$emit('save', this.role.role_id, userIds);
    },
    rowClass: function(isSelected) { return isSelected ? 'table-active' : ''; },

    isSelected: function(side, user) {
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      return set.has(user.user_id);
    },
    toggleRow: function(side, user) {
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      if (set.has(user.user_id)) set.delete(user.user_id);
      else set.add(user.user_id);

      if (side === 'left') this.selectedLeft = new Set(this.selectedLeft);
      else this.selectedRight = new Set(this.selectedRight);
    },
    toggleSelectAll: function(side) {
      var items = side === 'left' ? this.leftUsers : this.rightUsers;
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      var all = items.length > 0 && set.size === items.length;

      set.clear();
      if (!all) { items.forEach(function(u){ set.add(u.user_id); }); }

      if (side === 'left') this.selectedLeft = new Set(this.selectedLeft);
      else this.selectedRight = new Set(this.selectedRight);
    },
    clearSelections: function() {
      this.selectedLeft = new Set();
      this.selectedRight = new Set();
    },

    moveSelected: function(from, to) {
      var fromList = from === 'left' ? this.leftUsers : this.rightUsers;
      var toList = to === 'left' ? this.leftUsers : this.rightUsers;
      var sel = from === 'left' ? this.selectedLeft : this.selectedRight;

      var moving = [];
      var staying = [];
      for (var i = 0; i < fromList.length; i++) {
        var u = fromList[i];
        if (sel.has(u.user_id)) moving.push(u);
        else staying.push(u);
      }

      if (from === 'left') this.leftUsers = staying; else this.rightUsers = staying;
      if (to === 'left') this.leftUsers = this.leftUsers.concat(moving);
      else this.rightUsers = this.rightUsers.concat(moving);

      this.clearSelections();
    },

    onDragStart: function(from, user) {
      this.drag = { from: from, id: user.user_id };
    },
    onDropTo: function(target) {
      var from = this.drag.from;
      var id = this.drag.id;
      if (!from || !id || from === target) return;

      var src = from === 'left' ? this.leftUsers : this.rightUsers;
      var idx = -1;
      for (var i = 0; i < src.length; i++) {
        if (src[i].user_id === id) { idx = i; break; }
      }
      if (idx === -1) return;

      var moved = src.splice(idx, 1)[0];
      var dst = target === 'left' ? this.leftUsers : this.rightUsers;
      dst.push(moved);

      this.leftUsers = this.leftUsers.slice();
      this.rightUsers = this.rightUsers.slice();
      this.clearSelections();
      this.drag = { from: null, id: null };
    },
  },
  created: function() {
    this.resetFromProps();
  },
  watch: {
    initialLeft: { deep: true, handler: function() { this.resetFromProps(); } },
    initialRight:{ deep: true, handler: function() { this.resetFromProps(); } },
  },
};
</script>

<style scoped>
.table-responsive { min-height: 260px; border-top: 1px solid var(--bs-border-color, #dee2e6); }
.table-hover tbody tr { cursor: pointer; }
.card { box-shadow: none; }
.card-header { background: #f8f9fa; }
.table-active { background-color: rgba(13,110,253,.08); }
.center-controls {
  padding-top: 10px; /* match table header height */
}
</style>



// WEBPACK FOOTER //
// src/components/users/users/ManageUsers.vue